<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white shadow-lg rounded-lg w-full h-full sm:h-auto sm:max-w-4xl sm:mx-4 sm:my-8 overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="sticky top-0 bg-white z-10 p-4 border-b flex justify-between items-center rounded-t-lg">
        <h2 class="text-lg sm:text-2xl font-bold truncate">{{ product.title }}</h2>
        <button @click="close()" class="p-2 hover:bg-gray-100 rounded-full">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content (Scrollable) -->
      <div class="p-4 md:p-6 overflow-y-auto max-h-screen">
        <div class="grid gap-6 md:grid-cols-2">
          <!-- Left Side -->
          <div class="space-y-4">
            <div class="aspect-square relative rounded-lg overflow-hidden bg-gray-100">
              <img
                v-if="mainImageUrl"
                :src="mainImageUrl"
                :alt="product.title"

                class="object-cover w-full max-h-72"
              />
              <img
                v-else
                src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=="
                :alt="product.title"
                class="object-cover w-full h-full"
              />
            </div>

            <!-- Thumbnails -->
            <div class="grid grid-cols-4 gap-2">
              <div
                v-for="(img, index) in product.image"
                :key="index"
                class="aspect-square cursor-pointer border rounded-lg overflow-hidden"
                :class="{ 'border-blue-500': selectedImageIndex === index }"
                @click="selectImage(index)"
              >
                <img
                  :src="`https://be.ajakstore.com/storage/${img}`"
                  :alt="`${product.title} - ${index + 1}`"
                  class="w-full max-h-12 object-cover"
                />
              </div>
            </div>

            <div v-if="isMobile()">
              <div>
                <div class="mb-4">
                  <p class="text-2xl font-bold mb-4">Rp {{ formatPrice(product.price) }}</p>
                  <div class="space-y-2">
                    <p class="text-lg"><strong>Penjual:</strong> {{ product.user.name }}</p>
                    <p class="text-lg"><strong>Alamat:</strong> {{ product.user.address || '-' }}</p>
                    <p class="text-lg"><strong>Alamat:</strong> {{ product.stock }}</p>
                  </div>
                </div>
                <h3 class="font-semibold text-lg mb-2">Deskripsi Produk</h3>
                <p class="text-gray-600 whitespace-pre-wrap">{{ product.description }}</p>
                <div class="space-y-6">
                </div>
              </div>
            </div>
  
            <div v-if="!isMobile()">
              <div>
                <h3 class="font-semibold text-lg mb-2">Deskripsi Produk</h3>
              <p class="text-gray-600 whitespace-pre-wrap">{{ product.description }}</p>
              </div>
            </div>
            </div>

          <!-- Right Side -->
          <div class="space-y-6">
              <div  v-if="!isMobile()">
                <p class="text-2xl font-bold mb-4">Rp {{ formatPrice(product.price) }}</p>
                <div class="space-y-2">
                  <div class="seller-avatar flex items-center gap-2">
                    <img
                      :src="sellerAvatarUrl"
                      :alt="product.user.name"
                      class="w-10 h-10 rounded-full object-cover border border-gray-200"
                    />
                    <p class="text-lg"> {{ product.user.name }}</p>
                  </div>
                  <p class="text-lg"><strong>Alamat:</strong> {{ product.user.address || '-' }}</p>
                  <p class="text-lg"><strong>Stock:</strong> {{ product.stock }}</p>
                </div>
            </div>
            <!-- Specifications -->
            <div>
              <h3 class="font-semibold text-lg mb-4">Spesifikasi</h3>
              <div class="spec-grid">
                <div class="spec-row">
                  <span>SKU Produk</span>
                  <span>{{ product.sku }}</span>
                </div>
                <div v-if="product.made_in" class="spec-row">
                  <span>Made In</span>
                  <span>{{ product.made_in }}</span>
                </div>
                <div v-if="product.brand.brand_name" class="spec-row">
                  <span>Merek</span>
                  <span>{{ product.brand.brand_name }}</span>
                </div>
                <div v-if="product.type" class="spec-row">
                  <span>Tipe AC</span>
                  <span>{{ product.type.type_name }}</span>
                </div>
                <div v-if="product.power" class="spec-row">
                  <span>Daya Watt</span>
                  <span>{{ product.power }}</span>
                </div>
                <div v-if="product.btu" class="spec-row">
                  <span>BTU</span>
                  <span>{{ product.btu }}</span>
                </div>
                <div v-if="product.pk_power" class="spec-row">
                  <span>Nilai PK</span>
                  <span>{{ product.pk_power }} PK</span>
                </div>
                <div v-if="product.refrigrant_type" class="spec-row">
                  <span>Tipe Freon</span>
                  <span>{{ product.refrigrant_type }}</span>
                </div>
                <div v-if="product.pipe_size" class="spec-row">
                  <span>Ukuran Pipa (Liquid x Gas)</span>
                  <span>{{ product.pipe_size }}</span>
                </div>
                <div v-if="product.pipe_size" class="spec-row">
                  <span>Tipe Freon</span>
                  <span>{{ product.pipe_size }}</span>
                </div>
              </div>
            </div>

            <!-- Quantity and Add to Cart -->
            <div class="flex items-center justify-between space-x-4">
              <div class="flex items-center space-x-2">
                <button 
                  class="p-2 border rounded hover:bg-gray-50" 
                  @click="decrementQuantity" 
                  :disabled="quantity <= 1"
                >
                  -
                </button>
                <input 
                  type="number" 
                  class="w-16 text-center border rounded" 
                  v-model.number="quantity" 
                  :min="1" 
                  :max="product.stock"
                  @input="validateQuantity"
                />
                <button 
                  class="p-2 border rounded hover:bg-gray-50" 
                  @click="incrementQuantity"
                  :disabled="quantity >= product.stock"
                >
                  +
                </button>
              </div>
              <button
                class="text-white px-6 py-2 rounded-lg"
                :class="[
          isOutOfStock 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-blue-500 hover:bg-blue-600 text-white']"
                @click="addToCart"
                :disabled="quantity > product.stock && isOutOfStock"
              >
              
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: 'ProductDetailModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      quantity: 1,
      selectedImageIndex: 0,
      failedImages: new Set()
    }
  },
  computed: {
    isOutOfStock() {
      return this.product.stock === 0;
    },
    mainImageUrl() {
      if (Array.isArray(this.product.image) && this.product.image.length > 0 && !this.failedImages.has(this.selectedImageIndex)) {
        return `https://be.ajakstore.com/storage/${this.product.image[this.selectedImageIndex]}`
      }
      return null
    },
    sellerAvatarUrl() {
  if (this.product.user?.image) {
    return `https://be.ajakstore.com/storage/${this.product.user.image}`;
  }
  return 'https://placehold.co/32x32/lightgray/white?text=User';
}
  },
  methods: {
    close() {
    this.$router.replace({ query: {} })
  },
    incrementQuantity() {
      if (this.quantity < this.product.stock) {
        this.quantity++
      }
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--
      }
    },
    validateQuantity() {
      if (this.quantity < 1) {
        this.quantity = 1
      }
      if (this.quantity > this.product.stock) {
        this.quantity = this.product.stock
      }
    },
    addToCart() {
  this.$emit('add-to-cart', this.product, this.quantity)
  this.close() // Ganti this.$emit('close') menjadi this.close()
},
    formatPrice(price) {
      return new Intl.NumberFormat('id-ID').format(price)
    },
    selectImage(index) {
      this.selectedImageIndex = index
    },
    handleImageError() {
      this.failedImages.add(this.selectedImageIndex)
    },
    handleThumbnailError(index) {
      this.failedImages.add(index)
    },
    isMobile() {
      return screen.width <= 760;
    },
  }
}
</script>

<style scoped>
.spec-grid {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.spec-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #e5e7eb;
}

.spec-row:last-child {
  border-bottom: none;
}

.spec-row span {
  padding: 0.75rem 1rem;
}
.seller-avatar {
  display: flex;
  align-items: center;
  gap: 8px; /* Sesuaikan sesuai kebutuhan */
}
.spec-row:nth-child(odd) {
  background-color: #f9fafb;
}
@media (min-width: 760px) {
  .spec-row {
    grid-template-columns: 1fr 1fr;
  }
  
  .spec-row span {
    padding: 0.75rem 1rem;
  }
}
</style>
